import { Controller } from "@hotwired/stimulus";

// Inline tag editor for a saved influencer.
// - Renders chips (server-rendered).
// - "+ Tag" button reveals an input.
// - Enter on input adds tag; chip is appended optimistically and the
//   updated tag list is POSTed to /saved_influencers/:id/update_tags.
// - Click X on a chip removes it (also POSTs the updated list).
// - Escape or blur with empty value cancels the input.
export default class extends Controller {
  static targets = ["chips", "addBtn", "input"];
  static values = {
    id: Number,
    tags: Array, // current tag names
  };

  connect() {
    this._saving = false;
  }

  openInput(event) {
    event.preventDefault();
    if (!this.hasInputTarget) return;
    this.addBtnTarget.hidden = true;
    this.inputTarget.hidden = false;
    this.inputTarget.value = "";
    setTimeout(() => this.inputTarget.focus(), 0);
  }

  closeInput() {
    if (!this.hasInputTarget) return;
    this.inputTarget.hidden = true;
    this.inputTarget.value = "";
    this.addBtnTarget.hidden = false;
  }

  handleKey(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.commitInput();
    } else if (event.key === "Escape") {
      event.preventDefault();
      this.closeInput();
    }
  }

  handleBlur() {
    // Defer so a click on a button (e.g. add btn) registers first.
    setTimeout(() => {
      if (!this.hasInputTarget) return;
      if (this.inputTarget.value.trim()) {
        this.commitInput();
      } else {
        this.closeInput();
      }
    }, 120);
  }

  async commitInput() {
    const raw = this.inputTarget.value.trim();
    if (!raw) {
      this.closeInput();
      return;
    }

    // Allow comma-separated multi-add in one go.
    const newTags = raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const current = this.tagsValue || [];
    const merged = Array.from(new Set([...current, ...newTags]));

    this.inputTarget.value = "";
    this._renderTags(merged);
    this.closeInput();
    await this._save(merged);
  }

  async removeTag(event) {
    event.preventDefault();
    const tag = event.currentTarget.dataset.tag;
    const current = this.tagsValue || [];
    const next = current.filter((t) => t !== tag);
    this._renderTags(next);
    await this._save(next);
  }

  // ---- rendering -------------------------------------------------------

  _renderTags(tags) {
    if (!this.hasChipsTarget) return;
    this.tagsValue = tags;
    this.chipsTarget.innerHTML = tags
      .map((tag) => this._chipMarkup(tag))
      .join("");
    // Update the "Add tag" label visibility (hide when chips exist).
    const label = this.element.querySelector(
      ".saved-influencer-tags__add-label"
    );
    if (label) label.textContent = tags.length ? "" : "Add tag";
  }

  _chipMarkup(tag) {
    const safe = this._escape(tag);
    return (
      `<span class="saved-influencer-tags__chip" data-tag="${safe}">` +
      `<span class="saved-influencer-tags__chip-name">${safe}</span>` +
      `<button type="button" class="saved-influencer-tags__chip-remove" title="Remove tag" data-tag="${safe}" data-action="click->saved-influencer-tags#removeTag">` +
      `<i class="icon times"></i>` +
      `</button>` +
      `</span>`
    );
  }

  _escape(s) {
    return String(s).replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[c])
    );
  }

  // ---- network ---------------------------------------------------------

  async _save(tags) {
    if (this._saving) return;
    this._saving = true;
    const csrf =
      document.querySelector('meta[name="csrf-token"]')?.content || "";
    try {
      await fetch(`/saved_influencers/${this.idValue}/update_tags`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf,
        },
        credentials: "same-origin",
        body: JSON.stringify({ tag_list: tags.join(", ") }),
      });
    } catch {
      // swallow; a follow-up edit will retry
    } finally {
      this._saving = false;
    }
  }
}
